import Alert from '@mui/material/Alert';
import type { AlertColor } from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import { Theme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { noop } from 'lodash';
import { ReactElement } from 'react';

import { BlockedDoubleClickLoadingButton } from '../buttons/BlockedDoubleClickLoadingButton';

export interface CustomSeverityDialogProps {
	acceptButtonLabel: string;
	content: string | ReactElement;
	declineButtonLabel?: string;
	disabledAcceptButton?: boolean;
	disabledDeclineButton?: boolean;
	headerText: string;
	isOpen: boolean;
	onAccept: () => void;
	onDecline?: () => void;
	severity?: AlertColor;
	loading?: boolean;
}

export const CustomSeverityDialog = ({
	acceptButtonLabel,
	content,
	declineButtonLabel,
	disabledAcceptButton = false,
	disabledDeclineButton = false,
	headerText,
	isOpen,
	onAccept,
	onDecline = noop,
	severity = 'warning',
	loading = false,
}: CustomSeverityDialogProps): React.JSX.Element => {
	const fullScreen = useMediaQuery((theme: Theme) =>
		theme.breakpoints.only('xs')
	);

	return (
		<Dialog
			open={isOpen}
			fullScreen={fullScreen}
			sx={{ whiteSpace: 'pre-line' }}
			onClose={disabledDeclineButton ? noop : onDecline}>
			<DialogContent>
				<Alert severity={severity}>
					<AlertTitle variant="h6">{headerText}</AlertTitle>
					{content}
				</Alert>
			</DialogContent>
			<DialogActions
				sx={{
					justifyContent: declineButtonLabel
						? 'space-between'
						: 'center',
				}}>
				{declineButtonLabel && (
					<Button
						variant="outlined"
						color="secondary"
						disableElevation
						disabled={disabledDeclineButton}
						onClick={onDecline}>
						{declineButtonLabel}
					</Button>
				)}
				<BlockedDoubleClickLoadingButton
					variant="contained"
					color="secondary"
					disableElevation
					disabled={disabledAcceptButton}
					onClick={onAccept}
					loading={loading}>
					{acceptButtonLabel}
				</BlockedDoubleClickLoadingButton>
			</DialogActions>
		</Dialog>
	);
};

export default CustomSeverityDialog;
